import React, { useState, FC } from "react";
import { read, utils } from "xlsx";
import { Upload, Button, message, notification, Flex } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { axiosInstance } from "@/utilities";
import { API_URL_CONTACTS_IMPORT } from "@/services/XCONS_ENV";

interface ContactsImportButtonProps {
  formOk: boolean;
  configXlsData: any;
  storeCode: string;
  refetchTableXls: () => void;
}

/**
 * Lee el XLS en el front, aplica la config de mapeo de columnas (first_line +
 * columna→campo) para armar los `items[]` de contactos y los postea al backend.
 * Clon de `UpdateButton` (productos) con los campos de contacto.
 */
export const ContactsImportButton: FC<ContactsImportButtonProps> = React.memo(
  ({ formOk = false, configXlsData: configHeaders, storeCode, refetchTableXls }) => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [uploadKey, setUploadKey] = useState<number>(0);

    const toOptStr = (v: any) =>
      v === undefined || v === null || String(v).trim() === ""
        ? undefined
        : String(v).trim();

    // Arma un bloque de dirección { campo: valor } a partir del mapa de columnas
    // de la config ({ campo: header }) y la fila del XLS. Devuelve undefined si
    // no hay ningún dato (para no mandar un bloque vacío al backend).
    const buildAddress = (
      row: Record<string, any>,
      colMap: Record<string, string> | undefined | null,
    ): Record<string, string> | undefined => {
      if (!colMap || typeof colMap !== "object") return undefined;
      const out: Record<string, string> = {};
      for (const [field, header] of Object.entries(colMap)) {
        if (!header) continue;
        const v = toOptStr(row[header as string]);
        if (v !== undefined) out[field] = v;
      }
      return Object.keys(out).length ? out : undefined;
    };

    const handleUpload = async (options: any) => {
      setIsLoading(true);
      const { file, onError } = options;
      const reader = new FileReader();
      const { firstLine } = configHeaders?.data ?? {};

      const now = new Date();
      const pad = (n: number) => n.toString().padStart(2, "0");
      const stamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}-${pad(now.getMinutes())}-${pad(now.getSeconds())}`;
      const newFileName = `${file.name.split(".").slice(0, -1).join(".")}_${stamp}.${file.name.split(".").pop()}`;

      reader.onload = (event) => {
        try {
          const binaryStr = event.target?.result as ArrayBuffer;
          const bytes = new Uint8Array(binaryStr);
          let binary = "";
          for (let i = 0; i < bytes.byteLength; i += 8192) {
            binary += String.fromCharCode.apply(
              null,
              bytes.subarray(i, i + 8192) as unknown as number[],
            );
          }
          const fileBase64 = btoa(binary);
          const workbook = read(binaryStr, { type: "array" });
          const worksheet = workbook.Sheets[workbook.SheetNames[0]];
          const headers: any = (
            utils.sheet_to_json(worksheet, { header: firstLine })[0] as string[]
          ).map((h) => (typeof h === "string" ? h.trim() : h));

          const cfg = configHeaders.data;
          const configHeadersArray = Object.values(cfg).filter(
            (h) => typeof h === "string" && h.trim() !== "",
          );
          const matchedHeaders = headers.filter((h: string) =>
            configHeadersArray.includes(h),
          );

          const customColumns: string[] = (
            Array.isArray(cfg.customColumns) ? cfg.customColumns : []
          ).filter((col: string) => headers.includes(col));

          if (matchedHeaders.length === 0) {
            message.error("Ninguna columna del XLS coincide con la configuración");
            setUploadKey((k) => k + 1);
            setIsLoading(false);
            return;
          }

          const items = utils
            .sheet_to_json(worksheet, { header: headers, range: firstLine })
            .map((row: any) => ({
              firstname: toOptStr(row[cfg.firstname]),
              lastname: toOptStr(row[cfg.lastname]),
              email: toOptStr(row[cfg.email]),
              whatsapp: toOptStr(row[cfg.whatsapp]),
              description: toOptStr(row[cfg.description]),
              group: toOptStr(row[cfg.group]),
              customFields: customColumns.reduce(
                (acc: Record<string, string>, col: string) => {
                  const v = row[col];
                  if (v !== undefined && v !== null && String(v).trim() !== "") {
                    acc[col] = String(v).trim();
                  }
                  return acc;
                },
                {} as Record<string, string>,
              ),
              shippingAddress: buildAddress(row, cfg.shippingColumns),
              billingAddress: buildAddress(row, cfg.billingColumns),
            }));

          message.success(`Archivo "${file.name}" procesado`);
          setUploadKey((k) => k + 1);
          uploadToAPI(newFileName, items, fileBase64);
        } catch (error) {
          message.error("Error procesando el archivo");
          setUploadKey((k) => k + 1);
          setIsLoading(false);
          onError?.(null, file);
        }
      };

      reader.readAsArrayBuffer(file);
    };

    const uploadToAPI = async (name: string, items: any[], fileBase64?: string) => {
      try {
        const response = await axiosInstance.post(
          `${API_URL_CONTACTS_IMPORT}/${storeCode}`,
          { name, items, fileBase64 },
        );
        const errorCount: number = response?.data?.validationErrors ?? 0;
        if (errorCount > 0) {
          notification.warning({
            message: "Importación creada",
            description: `${errorCount} fila${errorCount !== 1 ? "s" : ""} con errores de validación fueron ignoradas. Revisá el detalle en el historial.`,
          });
        } else {
          notification.success({ message: "Importación creada" });
        }
        refetchTableXls();
      } catch (error: any) {
        notification.error({
          message: "Error al importar",
          description: error.message,
        });
      } finally {
        setIsLoading(false);
      }
    };

    const handleBeforeUpload = (file: any) => {
      const limitMB = 15;
      const isLessThanLimit = file.size / 1024 / 1024 < limitMB;
      if (!isLessThanLimit) {
        message.error(
          `El archivo "${file.name}" excede el tamaño máximo permitido de ${limitMB}MB`,
        );
      }
      return isLessThanLimit ? true : Upload.LIST_IGNORE;
    };

    return (
      <Flex vertical>
        <Upload
          key={uploadKey}
          customRequest={handleUpload}
          beforeUpload={handleBeforeUpload}
          maxCount={1}
          accept=".xls, .xlsx"
          showUploadList={false}
        >
          <Button
            disabled={!formOk}
            type="primary"
            size="middle"
            icon={<UploadOutlined />}
            iconPosition="end"
            loading={isLoading}
          >
            {isLoading ? "Cargando…" : "Cargar XLS"}
          </Button>
        </Upload>
      </Flex>
    );
  },
);
